import { Suspense, lazy, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useRouter } from '@tanstack/react-router';
import { Button } from '@jeraisonne/ui';
import { PaperColumn, PaperPage } from '@jeraisonne/ui/paper';
import { AlertCircle, Loader2, X } from 'lucide-react';
import type { SchoolLevel } from '@jeraisonne/db';
import {
  LevelSelectionScreen,
  ProfileSelectionScreen,
} from '@/features/demo/screens/level-selection-screen';
import { DemoEndScreen } from '@/features/demo/screens/demo-end-screen';
import { trackDemoEvent } from '@/features/demo/tracking';
import { TrousseScreen } from '@/features/demo/screens/trousse-screen';
import { swapScreens } from '@/features/demo/screens/screen-swap';
import { FunScreen } from '@/features/demo/screens/fun-screen';
import { MovesRecapScreen } from '@/features/demo/screens/moves-recap-screen';
import type { KitToken } from '@/features/demo/screens/kit-stage';
import type { GuidedResult } from '@/features/demo/guided/guided-round';
import { demoExerciseFor } from '@/features/demo/demo-exercises';
import { LEVEL_LABEL } from '@/features/demo/types';
import { loadGuidedQuestion, type GuidedQuestion } from '@/features/demo/guided/guided-question';
import { ExercisePlayerV2 } from '@/features/exercise-player-v2';
import { markPaperTutorialSeen } from '@/features/scratch-sheet/sheet-tutorial';
import { markScratchTutorialFinished } from '@/features/scratch-tutorial/first-use';
import {
  clearDecouverteProfile,
  readDecouverteProfile,
  saveDecouverteProfile,
  type DecouverteProfile,
} from '@/features/decouverte/decouverte-profile';
import { markDecouverteSeen } from '@/features/decouverte/decouverte-seen';
import { trackAppEvent } from '@/lib/analytics/track';

// le moteur v2 (packs compilés) n'est chargé qu'ici
const GuidedRound = lazy(() => import('@/features/demo/guided/guided-round'));

type Phase = 'level' | 'kit' | 'play' | 'recap' | 'fun' | 'end';

/**
 * Route `/demo` — démo sans compte, en trois temps :
 *
 *  1. le visiteur touche SA classe (6e → Terminale) ;
 *  2. un écran le prépare : « Raisonne pas à pas · Laisse-toi guider » (une fois par visite) ;
 *  3. il joue la vraie question de démo de cette classe, GUIDÉE : la feuille du moteur v2 sous la question, un
 *     coach collé au clavier qui montre où toucher (l'élément pulse en doré ; c'est toujours l'élève qui appuie)
 *     et fait sentir la puissance du moteur — il connaît le cours (l'ampoule), repère l'erreur classique et
 *     l'explique, propose les valeurs et les résultats, vérifie chaque ligne, marque la réponse trouvée. Le coach
 *     est le même pour toutes les classes (`coach.ts`) : une question qu'il ne sait pas mener jusqu'à la réponse
 *     se joue sans guide (player V2 en mode démo) ;
 *  4. l'inscription GRATUITE (aucun prix).
 *
 * Aucune authentification, rien n'est enregistré.
 */
export function DemoPage() {
  const navigate = useNavigate();
  const router = useRouter();
  const [phase, setPhase] = useState<Phase>('level');
  // qui joue : l'accueil `/decouverte` l'a souvent déjà dit ; sinon la démo le demande avant la classe
  const [profile, setProfile] = useState<DecouverteProfile | null>(() => readDecouverteProfile());
  const [level, setLevel] = useState<SchoolLevel | null>(null);
  const [question, setQuestion] = useState<GuidedQuestion | null>(null);
  const [loadError, setLoadError] = useState(false);
  // la question se joue sans guide (pas de feuille, ou le coach ne la mène pas au bout)
  const [unguided, setUnguided] = useState(false);
  // incrémenté pour rejouer la question depuis le début
  const [round, setRound] = useState(0);
  // l'écran « Raisonne pas à pas · Laisse-toi guider » a été passé (une fois par visite)
  const [started, setStarted] = useState(false);
  // les Moves gagnés dans la question : l'écran récap les montre avant de parler de l'offre
  const [moves, setMoves] = useState<readonly KitToken[]>([]);
  // la trousse s'envole vers sa place dans l'exercice, déjà monté dessous
  const [leaving, setLeaving] = useState(false);

  // Le relais : la trousse 3D sort de l'écran par la droite (720 ms), celle de l'exercice attend
  // hors cadre puis entre par la droite dès qu'elle est sortie — on ne voit jamais les deux.
  useEffect(() => {
    if (!leaving) return;
    const body = document.body;
    const clear = () => {
      delete body.dataset.jrFlying;
      delete body.dataset.jrLanding;
    };
    body.dataset.jrFlying = '1';
    const land = window.setTimeout(() => {
      delete body.dataset.jrFlying;
      body.dataset.jrLanding = '1';
    }, 620);
    // ⚠️ `leaving` dure PLUS que l'entrée (son effet se rejoue et `clear()` retirerait le marqueur
    // en pleine glissade) : la trousse de l'exercice finit de se poser avant qu'on démonte l'écran.
    const end = window.setTimeout(() => setLeaving(false), 1060);
    return () => {
      window.clearTimeout(land);
      window.clearTimeout(end);
      clear();
    };
  }, [leaving]);

  useEffect(() => {
    trackDemoEvent({ type: 'demo_started' });
    // Ancienne démo (échauffement V1) : son état local ne sert plus.
    try {
      window.localStorage.removeItem('jeraisonne_demo_state');
    } catch {
      // stockage indisponible (navigation privée) : rien à effacer
    }
  }, []);

  // Pages « virtuelles » : l'entonnoir de la démo dans les statistiques.
  useEffect(() => {
    if (phase === 'level') trackAppEvent('page_view', profile ? '/demo/classe' : '/demo/profil');
    if (phase === 'kit') trackAppEvent('page_view', '/demo/trousse');
    if (phase === 'play') trackAppEvent('page_view', '/demo/question');
    if (phase === 'recap') trackAppEvent('page_view', '/demo/moves');
    if (phase === 'fun') trackAppEvent('page_view', '/demo/jeu');
    if (phase === 'end') trackAppEvent('page_view', '/demo/fin');
    window.scrollTo({ top: 0 });
  }, [phase, profile]);

  useEffect(() => {
    if (!level) return;
    const exerciseId = demoExerciseFor(level);
    let cancelled = false;
    setQuestion(null);
    setLoadError(false);
    setUnguided(false);
    if (!exerciseId) {
      setLoadError(true);
      return;
    }
    loadGuidedQuestion(exerciseId)
      .then((q) => {
        if (cancelled) return;
        if (!q) setLoadError(true);
        else {
          setQuestion(q);
          setUnguided(!q.model);
        }
      })
      .catch(() => {
        if (!cancelled) setLoadError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [level]);

  // L'écran de la trousse laisse le temps de charger le moteur v2 (le même module que `GuidedRound`)
  // ET la question : « C'est parti » attend les deux, pour que la feuille soit là d'un coup.
  const ready = useRef<Promise<unknown> | null>(null);
  useEffect(() => {
    if (!level) return;
    ready.current = import('@/features/demo/guided/guided-round').catch(() => null);
  }, [level]);

  const finish = useCallback(() => {
    trackDemoEvent({ type: 'demo_completed' });
    // les gestes sont acquis : la bulle de prise en main du player ne se rejoue pas
    markPaperTutorialSeen();
    swapScreens(() => setPhase('fun'));
  }, []);
  // l'exercice guidé EST la prise en main du brouillon : pas de didacticiel à l'inscription sur cet appareil
  const finishGuided = useCallback(
    (r: GuidedResult) => {
      markScratchTutorialFinished();
      if (r.moves.length > 0) {
        // un Move gagné : on montre ce qu'il emporte avant l'écran d'inscription
        trackDemoEvent({ type: 'demo_completed' });
        markPaperTutorialSeen();
        swapScreens(() => {
          setMoves(r.moves);
          setPhase('recap');
        });
        return;
      }
      finish();
    },
    [finish],
  );
  const playUnguided = useCallback(() => setUnguided(true), []);

  function pickLevel(selected: SchoolLevel) {
    swapScreens(() => {
      setLevel(selected);
      setRound((r) => r + 1);
      setLeaving(false);
      // la trousse ne se découvre qu'une fois : une autre classe reprend directement la question
      setPhase(started ? 'play' : 'kit');
    });
    trackDemoEvent({ type: 'demo_level_selected', level: selected });
  }

  function signup() {
    trackDemoEvent({ type: 'demo_signup_clicked' });
    // le profil est demandé avant la classe : il est connu (un parent reste parent)
    if (!readDecouverteProfile()) saveDecouverteProfile(profile ?? 'student');
    markDecouverteSeen();
    void navigate({ to: '/login', search: { inscription: 1 } });
  }

  /**
   * La fin de la démo : « OK ! je veux m'inscrire » passe par la présentation de l'app
   * (retour de Philippe du 5/11/2026) — c'est elle qui demande qui on est, puis inscrit.
   */
  function discover() {
    trackDemoEvent({ type: 'demo_signup_clicked' });
    void navigate({ to: '/decouverte' });
  }

  function login() {
    markDecouverteSeen();
    void navigate({ to: '/login' });
  }

  function quit() {
    if (window.history.length > 1) router.history.back();
    else void navigate({ to: '/' });
  }

  if (phase === 'recap') {
    return (
      <MovesRecapScreen
        moves={moves}
        onSignup={signup}
        onContinue={() => swapScreens(() => setPhase('fun'))}
      />
    );
  }

  if (phase === 'fun') {
    return (
      <FunScreen
        level={level}
        onSignup={discover}
        onOtherLevel={() => swapScreens(() => setPhase('level'))}
      />
    );
  }

  if (phase === 'end') {
    return (
      <div className="min-h-dvh bg-[#061433] sm:flex sm:items-center sm:justify-center sm:py-8">
        <DemoEndScreen
          onSignup={signup}
          onLogin={login}
          restartLabel="Essayer une autre classe"
          onRestart={() => swapScreens(() => setPhase('level'))}
        />
      </div>
    );
  }

  if (phase === 'level' || !level) {
    return (
      <Shell>
        <DemoTopBar label="Démo · sans compte" onClose={quit} />
        <div className="mt-4">
          {profile === null ? (
            <ProfileSelectionScreen
              onPick={(p) => {
                saveDecouverteProfile(p);
                swapScreens(() => setProfile(p));
              }}
            />
          ) : (
            <LevelSelectionScreen
              onSelect={pickLevel}
              onSignup={signup}
              profile={profile}
              onChangeProfile={() => {
                clearDecouverteProfile();
                swapScreens(() => setProfile(null));
              }}
            />
          )}
        </div>
      </Shell>
    );
  }

  const classLabel = LEVEL_LABEL[level];
  const topBar = (
    <DemoTopBar
      label={`${unguided ? 'Une question' : 'Exercice guidé'} · ${classLabel} · sans compte`}
      onClose={quit}
      action={{ label: 'Passer', onClick: () => swapScreens(() => setPhase('end')) }}
    />
  );

  // La trousse, en grand : elle prépare la question (et laisse le temps de charger le moteur v2) —
  // d'où sa place AVANT l'écran d'attente, qu'on ne doit jamais voir passer entre les deux.
  // `started` la saute quand on revient pour une autre classe : on ne la redécouvre qu'une fois.
  // ⚠️ Elle reste au MÊME endroit de l'arbre pendant son vol : la feuille se monte dessous et la
  // trousse 3D poursuit sa course au lieu d'être recréée.
  const kit = phase === 'kit' && !started;

  // une question qui ne charge pas passe AVANT la trousse : jamais « C'est parti » vers une feuille absente
  if (loadError) {
    return (
      <Shell>
        {topBar}
        <div className="mx-auto mt-6 max-w-lg space-y-4">
          <div className="bg-error-50 border-error-100 flex items-start gap-2 rounded-xl border px-4 py-3">
            <AlertCircle className="text-error-600 mt-0.5 h-4 w-4 shrink-0" />
            <p className="text-error-700 text-sm">Impossible de charger la question. Réessaie.</p>
          </div>
          <Button onClick={() => swapScreens(() => setPhase('level'))} fullWidth>
            Choisir une autre classe
          </Button>
        </div>
      </Shell>
    );
  }

  if (!question && !kit) {
    return (
      <Shell>
        {topBar}
        <div
          className="mx-auto mt-6 max-w-lg space-y-3"
          aria-busy="true"
          data-testid="demo-loading"
        >
          <div className="bg-ink-100 h-6 w-40 animate-pulse rounded" />
          <div className="bg-ink-100 h-32 animate-pulse rounded-2xl" />
          <div className="bg-ink-100 h-12 animate-pulse rounded-xl" />
        </div>
      </Shell>
    );
  }

  if (unguided && question) {
    return (
      <Shell>
        {topBar}
        <div className="mt-2">
          <ExercisePlayerV2
            key={round}
            exerciseId={question.exercise.exercise.id}
            demoMode
            demoPayload={{
              exercise: {
                ...question.exercise.exercise,
                chapter_id: question.exercise.exercise.chapter_id ?? undefined,
              },
              blocks: question.exercise.blocks,
              access: { allowed: true },
              attempt_id: null,
            }}
            onDemoComplete={finish}
          />
        </div>
      </Shell>
    );
  }

  return (
    <>
      {/* `question` est là dès que `kit` est faux (l'écran d'attente, plus haut, s'en assure) */}
      {!kit && question && (
        <PaperPage>
          <PaperColumn className="native:pt-[calc(env(safe-area-inset-top)+0.75rem)] pb-8 pt-3">
            {topBar}
            <Suspense
              fallback={
                <p
                  className="text-ink-600 mt-6 flex items-center gap-2 text-sm"
                  data-testid="demo-guided-loading"
                >
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                  Préparation de ta feuille…
                </p>
              }
            >
              <GuidedRound
                key={round}
                question={question}
                onFinished={finishGuided}
                onUnavailable={playUnguided}
                level={level}
              />
            </Suspense>
            {/* loin sous la feuille : rien de plus à l'écran pendant l'exercice */}
            <div className="mt-24 flex flex-wrap items-center justify-center gap-x-6">
              <button
                type="button"
                onClick={() => setRound((r) => r + 1)}
                className="text-ink-500 hover:text-ink-700 min-h-11 text-sm font-medium"
              >
                Recommencer l’exercice
              </button>
              <button
                type="button"
                onClick={() => swapScreens(() => setPhase('level'))}
                className="text-ink-500 hover:text-ink-700 min-h-11 text-sm font-medium"
              >
                Changer de classe
              </button>
            </div>
          </PaperColumn>
        </PaperPage>
      )}
      {(kit || leaving) && (
        <TrousseScreen
          level={level}
          leaving={leaving}
          onStart={() => {
            // la trousse reste à l'écran le temps que la feuille puisse se montrer d'un seul coup
            void (ready.current ?? Promise.resolve(null)).then(() => {
              setStarted(true);
              setPhase('play');
              setLeaving(true);
            });
          }}
        />
      )}
    </>
  );
}

/** Barre du haut de la démo : quitter, ce qu'on fait, une action à droite. */
function DemoTopBar({
  label,
  onClose,
  action,
}: {
  label: string;
  onClose: () => void;
  action?: { label: string; onClick: () => void };
}) {
  return (
    <div className="flex items-center gap-2" data-testid="demo-topbar">
      <button
        type="button"
        onClick={onClose}
        aria-label="Quitter"
        className="text-ink-600 hover:text-ink-900 -ml-2 flex h-11 w-11 shrink-0 items-center justify-center rounded-full"
      >
        <X className="h-5 w-5" aria-hidden />
      </button>
      <p className="text-primary-600 min-w-0 flex-1 truncate text-xs font-bold uppercase tracking-wider">
        {label}
      </p>
      {action && (
        <button
          type="button"
          onClick={action.onClick}
          className="text-primary-600 hover:text-primary-700 min-h-11 shrink-0 px-2 text-sm font-semibold"
        >
          {action.label}
        </button>
      )}
    </div>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-dvh bg-transparent">
      <div className="native:pt-[env(safe-area-inset-top)] mx-auto max-w-2xl px-4 pb-[max(env(safe-area-inset-bottom),1.5rem)] pt-3 sm:py-8">
        {children}
      </div>
    </div>
  );
}
